import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import {
  getApiErrorMessage,
  Medication,
  searchMedications,
} from "src/apis/prontuario";
import {
  getMedicationActiveIngredient,
  getMedicationConcentration,
  getMedicationForm,
  getMedicationId,
  getMedicationName,
  getMedicationRoute,
} from "./utils";

type MedicationSearchComboboxProps = {
  disabled?: boolean;
  onSelect: (medication: Medication | null) => void;
  onError: (message: string) => void;
};

const MIN_SEARCH_LENGTH = 2;

const MedicationSearchCombobox: FC<MedicationSearchComboboxProps> = ({
  disabled = false,
  onSelect,
  onError,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedMedication, setSelectedMedication] = useState<Medication | null>(null);
  const [options, setOptions] = useState<Medication[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const canSearch = useMemo(
    () => inputValue.trim().length >= MIN_SEARCH_LENGTH,
    [inputValue]
  );

  useEffect(() => {
    if (!canSearch) {
      setOptions([]);
      setLoading(false);
      return;
    }

    let isActive = true;
    const searchTimeout = window.setTimeout(async () => {
      setLoading(true);

      try {
        const medications = await searchMedications(inputValue.trim());

        if (isActive) {
          setOptions(medications);
        }
      } catch (err: unknown) {
        if (isActive) {
          setOptions([]);
          onError(getApiErrorMessage(err, "Não foi possível buscar medicamentos."));
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      isActive = false;
      window.clearTimeout(searchTimeout);
    };
  }, [canSearch, inputValue, onError]);

  return (
    <Autocomplete<Medication, false, false, false>
      disabled={disabled}
      options={options}
      value={selectedMedication}
      loading={loading}
      inputValue={inputValue}
      filterOptions={(availableOptions) => availableOptions}
      noOptionsText={canSearch ? "Nenhum medicamento encontrado." : "Digite ao menos 2 caracteres."}
      getOptionLabel={(option) => getMedicationName(option)}
      isOptionEqualToValue={(option, value) =>
        getMedicationId(option) === getMedicationId(value)
      }
      onInputChange={(_, value) => {
        setInputValue(value);
      }}
      onChange={(_, value) => {
        setSelectedMedication(value);
        onSelect(value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Buscar medicamento"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={getMedicationId(option)}>
          <Box>
            <Typography fontWeight={700}>{getMedicationName(option)}</Typography>
            <Typography variant="body2">
              Princípio ativo: {getMedicationActiveIngredient(option)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getMedicationConcentration(option)} · {getMedicationForm(option)} ·{" "}
              {getMedicationRoute(option)}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
};

export default MedicationSearchCombobox;
